import { useRef } from "react";

import { AttributeType, IAttribute, IRelationObject } from "@interfaces/attribute.interface";
import { DatePicker, Input, LocationAutocomplete, MaskInput } from "@ui/FormElements";
import { isMaskInputVariant } from "@ui/FormElements/MaskInput.config";
import { SearchableSelect } from "@ui/Select";

import {
  DEFAULT_CURRENCY_SYMBOL,
  DROPDOWN_ATTRIBUTE_TYPES,
  isRelationAttribute,
} from "./constants";
import { RelationAttributeInput } from "./RelationAttribute";
import {
  formatAttributeDate,
  getAttributeInputType,
  getAttributePlaceholder,
  parseAttributeDate,
} from "./utils";

type Option = { value: string; label: string };

interface AttributeFieldProps {
  attribute: IAttribute;
  // For dropdown types this is comma-joined option ids.
  value: string;
  onChange: (value: string) => void;
  // Fires when the value is saved (input blur / select change) — used for inline save.
  onSave?: (value: string) => void;
  disabled?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  // Prefix shown on CURRENCY attributes.
  currencySymbol?: string;
  // RELATION only: the tagged records; `value` is unused for these fields.
  relations?: IRelationObject[];
  onRelationsChange?: (next: IRelationObject[]) => void;
  // RELATION only: the settled set on close — one write per batch.
  onRelationsSave?: (next: IRelationObject[]) => void;
  // RELATION only: records the search must not offer.
  excludedRelationIds?: string[];
}

export function AttributeField({
  attribute,
  value,
  onChange,
  onSave,
  disabled,
  isInvalid,
  errorMessage,
  currencySymbol = DEFAULT_CURRENCY_SYMBOL,
  relations,
  onRelationsChange,
  onRelationsSave,
  excludedRelationIds,
}: AttributeFieldProps) {
  const placeholder = getAttributePlaceholder(attribute);
  // Value when the input gained focus — lets us skip saving an unchanged field on blur.
  const focusValueRef = useRef(value);

  const saveOnFocusChange = {
    onFocus: () => {
      focusValueRef.current = value;
    },
    onBlur: () => {
      if (value !== focusValueRef.current) onSave?.(value);
    },
  };

  // For fields that emit a value only once it's saved (dropdowns, location), every change is a
  // save — they can't use the focus/blur diff above, whose `value` is still the pre-edit one.
  const save = (next: string) => {
    if (next === value) return;
    onChange(next);
    onSave?.(next);
  };

  if (isRelationAttribute(attribute.type)) {
    return (
      <RelationAttributeInput
        attribute={attribute}
        value={relations ?? []}
        isInvalid={isInvalid}
        errorMessage={errorMessage}
        onChange={(next) => onRelationsChange?.(next)}
        onSave={(next) => onRelationsSave?.(next)}
        excludedIds={excludedRelationIds}
        disabled={disabled}
      />
    );
  }

  if (DROPDOWN_ATTRIBUTE_TYPES.includes(attribute.type)) {
    const options: Option[] = (attribute.dropdown_options ?? []).map((o) => ({
      value: o.id,
      label: o.title,
    }));
    const selectedIds = value ? value.split(",").filter(Boolean) : [];

    if (attribute.type === AttributeType.MULTISELECT) {
      return (
        <SearchableSelect<Option, true>
          isMulti
          isClearable
          className="w-full"
          placeholder={placeholder}
          isDisabled={disabled}
          isInvalid={isInvalid}
          errorMessage={errorMessage}
          options={options}
          value={options.filter((o) => selectedIds.includes(o.value))}
          onChange={(opts) => save((opts as Option[]).map((o) => o.value).join(","))}
        />
      );
    }

    return (
      <SearchableSelect<Option>
        isClearable
        className="w-full"
        placeholder={placeholder}
        isDisabled={disabled}
        isInvalid={isInvalid}
        errorMessage={errorMessage}
        options={options}
        value={options.find((o) => o.value === value) ?? null}
        onChange={(opt) => save((opt as Option | null)?.value ?? "")}
      />
    );
  }

  if (attribute.type === AttributeType.DATE) {
    return (
      <DatePicker
        selectedDate={parseAttributeDate(value)}
        placeholderText={placeholder}
        dateFormat="MM/dd/yyyy"
        strictParsing
        popperPlacement="bottom"
        disabled={disabled}
        inputProps={{ isInvalid, errorMessage }}
        onChange={(date) => save(formatAttributeDate(date))}
      />
    );
  }

  if (attribute.type === AttributeType.LOCATION) {
    return (
      <LocationAutocomplete
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        isInvalid={isInvalid}
        errorMessage={errorMessage}
        onChange={save}
      />
    );
  }

  if (isMaskInputVariant(attribute.type)) {
    return (
      <MaskInput
        variant={attribute.type}
        value={value}
        prefix={attribute.type === AttributeType.CURRENCY ? currencySymbol : undefined}
        disabled={disabled}
        isInvalid={isInvalid}
        errorMessage={errorMessage}
        onChange={onChange}
        {...saveOnFocusChange}
      />
    );
  }

  return (
    <Input
      isFullWidth
      type={getAttributeInputType(attribute.type)}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      isInvalid={isInvalid}
      errorMessage={errorMessage}
      onChange={(e) => onChange(e.target.value)}
      {...saveOnFocusChange}
    />
  );
}
